import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, apiClient, getUserInfo, getCurrentTenantUserId } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { checkPermission } from '../../utils/permissionUtils';
import { eventToFormInitialData } from '../../utils/eventFormInitialData';
import Calendar, { CalendarEvent } from '../../components/Calendar';
import EventStatusBadge from '../../components/EventStatusBadge';
import EventStatusActions from '../../components/EventStatusActions';
import { reselectById } from '../../utils/eventUtils';
import Modal from '../../components/Modal';
import AttendanceOverview from '../../components/AttendanceOverview';
import EventComments from '../../components/messaging/EventComments';
import EventAnnouncements from '../../components/messaging/EventAnnouncements';
import BroadcastModal from '../../components/messaging/BroadcastModal';
import ExportModal from '../../components/ExportModal';
import { getEventExportFields, ExportableData } from '../../utils/exportUtils';
import { memberFields } from '../../utils/entityFields';
import EventForm, { IEventFormData } from '../../components/forms/EventForm';
import ToggleSwitch from '../../components/forms/ToggleSwitch';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import EmptyState from '../../components/ui/EmptyState';
import { ConfirmModal } from '../../components/ui';
import CollapsibleWidget from '../../components/CollapsibleWidget';
import RequirementsWidget from '../../components/RequirementsWidget';
import EventRequirementsConfig from '../../components/EventRequirementsConfig';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import PageHeader from '../../components/PageHeader';
import './EventCalendarPage.css';
import { translateError } from '../../utils/errorMessages';

interface IEventTemplate {
  _id: string;
  name: string;
  description: string;
  firstOccurrence: string;
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  recurringPattern: string;
  isActive: boolean;
  members: (string | number)[];
  groups: (string | number)[];
  responsible?: string;
  defaultAttendance?: boolean | null;
  price?: number;
}

type ModalStep = 'closed' | 'day-events' | 'event-edit' | 'choose-source' | 'select-template' | 'event-form';

interface MemberAttendance {
  _id: string;
  name: string;
  color: string;
  status: 'attending' | 'not-attending' | 'maybe';
  user?: {
    _id: string;
    name: string;
    email: string;
    admin?: boolean;
  };
  groups?: { _id: string; name: string }[];
}

interface GroupAttendance {
  _id: string;
  name: string;
  members: MemberAttendance[];
}

interface AttendanceData {
  eventId: string;
  defaultAttendance: boolean | null;
  groups: GroupAttendance[];
  directMembers: MemberAttendance[];
}

interface ISelectOption {
  _id: string | number;
  name: string;
  preferences?: { themeColor?: string };
  role?: {
    type?: string;
    name: string;
    permissions?: Record<string, number>;
  };
}

const EventCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [templates, setTemplates] = useState<IEventTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permission checks
  const canCreate = hasPermission('events:write');
  const canEdit = hasPermission('events:write');
  const canDelete = hasPermission('events:delete');
  const canViewUsers = hasPermission('users:read');

  // Selection options (pre-fetched for EventForm)
  const [allMembers, setAllMembers] = useState<ISelectOption[]>([]);
  const [allGroups, setAllGroups] = useState<ISelectOption[]>([]);
  const [allUsers, setAllUsers] = useState<ISelectOption[]>([]);
  const [admins, setAdmins] = useState<ISelectOption[]>([]);

  // Calendar state
  const [currentYear, setCurrentYear] = useState(() => new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(() => new Date().getMonth());

  // Modal state
  const [modalStep, setModalStep] = useState<ModalStep>('closed');
  // Escape/backdrop must not silently bin an in-progress event edit.
  const [formDirty, setFormDirty] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedDayEvents, setSelectedDayEvents] = useState<CalendarEvent[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<IEventTemplate | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'attendance' | 'comments'>('form');

  // Form initial data (for EventForm component)
  const [formInitialData, setFormInitialData] = useState<Partial<IEventFormData> | undefined>(undefined);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filter state
  const [filterAdmin, setFilterAdmin] = useState<string>('');
  const [filterMine, setFilterMine] = useState(false);

  // Broadcast state
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState<ExportableData[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Map<string, boolean | null>>(new Map());
  const [exportLoading, setExportLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await api.events.list();
      const fresh = data as CalendarEvent[];
      setEvents(fresh);
      // `selectedEvent` is a separate copy taken when the modal opened, so a
      // status change left the open modal showing the old badge and button.
      setSelectedEvent((current) => reselectById(current, fresh));
    } catch (err: any) {
      setError(translateError(err, t));
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await api.eventTemplates.list();
      setTemplates(data as IEventTemplate[]);
    } catch (err: any) {
      // Silently fail for templates
    }
  }, []);

  const fetchSelectOptions = useCallback(async () => {
    try {
      const canReadMembers = hasPermission('members:read');
      const canReadGroups = hasPermission('groups:read');
      const [membersRes, groupsRes] = await Promise.all([
        canReadMembers ? api.members.list() : Promise.resolve({ data: [] as ISelectOption[] }),
        canReadGroups ? api.groups.list() : Promise.resolve({ data: [] as ISelectOption[] }),
      ]);
      setAllMembers(membersRes.data as ISelectOption[]);
      setAllGroups(groupsRes.data as ISelectOption[]);

      // Fetch users if user has permission
      if (canViewUsers) {
        try {
          const usersRes = await api.users.list();
          const usersData = usersRes.data as ISelectOption[];
          setAllUsers(usersData);
          // Filter users who can manage events (Admin/Owner role or events:write permission)
          setAdmins(usersData.filter(u =>
            u.role?.type === 'admin' ||
            u.role?.name === 'Owner' ||
            checkPermission(u.role?.permissions, 'events:write')
          ));
        } catch {
          // Ignore error - user might not have permission
        }
      }
    } catch (err: any) {
      // Silently fail for select options
    }
  }, [canViewUsers, hasPermission]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEvents(), fetchTemplates(), fetchSelectOptions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchEvents, fetchTemplates, fetchSelectOptions]);

  // Handle eventId query parameter (from notification click)
  useEffect(() => {
    const eventId = searchParams.get('eventId');
    if (eventId && events.length > 0 && modalStep === 'closed') {
      const event = events.find(e => e._id === eventId);
      if (event) {
        // Open the event modal and switch to comments tab
        setSelectedEvent(event);
        setFormInitialData(eventToFormInitialData(event));
        setActiveTab('comments'); // Open on comments tab since they clicked a comment notification
        setBroadcastOpen(false);
        setModalStep('event-edit');
        // Clear the query parameter
        setSearchParams({});
      }
    }
  }, [events, searchParams, modalStep, setSearchParams]);

  // Map events with responsible user's color
  const eventsWithColors = React.useMemo(() => {
    return events.map(event => {
      const responsible = event.responsible;
      if (responsible) {
        const responsibleId = typeof responsible === 'string' ? responsible : responsible?._id;
        if (responsibleId) {
          const responsibleUser = allUsers.find(u => u._id.toString() === responsibleId.toString());
          if (responsibleUser?.preferences?.themeColor && responsibleUser.preferences.themeColor !== '#ffffff') {
            return { ...event, color: responsibleUser.preferences.themeColor };
          }
        }
      }
      return event;
    });
  }, [events, allUsers]);

  // Filter events based on selected admin or "Mine" checkbox
  const filteredEvents = React.useMemo(() => {
    let filtered = eventsWithColors;

    // "Mine" takes precedence over dropdown filter.
    // event.responsible._id is a TENANT users.id, so compare against the tenant
    // user id (NOT getUserInfo()._id, which is the global account id).
    if (filterMine) {
      const myTenantId = getCurrentTenantUserId();
      filtered = filtered.filter(event => {
        const responsible = event.responsible;
        const responsibleId = typeof responsible === 'string' ? responsible : responsible?._id;
        return String(responsibleId) === myTenantId;
      });
    } else if (filterAdmin) {
      filtered = filtered.filter(event => {
        const responsible = event.responsible;
        const responsibleId = typeof responsible === 'string' ? responsible : responsible?._id;
        return String(responsibleId) === String(filterAdmin);
      });
    }

    return filtered;
  }, [eventsWithColors, filterAdmin, filterMine]);

  // Handle filter changes
  const handleFilterAdminChange = (adminId: string) => {
    setFilterAdmin(adminId);
    if (adminId) {
      setFilterMine(false); // Clear "Mine" when selecting from dropdown
    }
  };

  const handleFilterMineChange = (checked: boolean) => {
    setFilterMine(checked);
    if (checked) {
      setFilterAdmin(''); // Clear dropdown when checking "Mine"
    }
  };

  // Filter content for calendar header
  const filterContent = (
    <>
      <select
        value={filterAdmin}
        onChange={(e) => handleFilterAdminChange(e.target.value)}
        disabled={filterMine}
      >
        <option value="">{t('calendar.allAdmins')}</option>
        {admins.map((admin) => (
          <option key={admin._id} value={admin._id}>
            {admin.name}
          </option>
        ))}
      </select>
      <ToggleSwitch
        checked={filterMine}
        onChange={handleFilterMineChange}
        label={t('calendar.mine')}
      />
    </>
  );

  // Calendar navigation
  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  // Event click - show edit form in modal
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    // Set initial data for the EventForm
    setFormInitialData(eventToFormInitialData(event));
    setActiveTab('form');
    setBroadcastOpen(false);
    setModalStep('event-edit');
  };

  // Day click - show day events modal
  const handleDayClick = (date: Date, dayEvents: CalendarEvent[]) => {
    setSelectedDate(date);
    setSelectedDayEvents(dayEvents);
    setSelectedTemplate(null);
    setModalStep('day-events');
  };

  const handleCreateNewEvent = () => {
    if (!canCreate) return;
    setModalStep('choose-source');
  };

  const handleChooseFromScratch = () => {
    // Set default times based on selected date
    if (selectedDate) {
      const startDate = new Date(selectedDate);
      startDate.setHours(9, 0, 0, 0);
      const endDate = new Date(selectedDate);
      endDate.setHours(10, 0, 0, 0);

      setFormInitialData({
        name: '',
        description: '',
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        members: [],
        groups: [],
        defaultAttendance: null,
        responsible: '',
        price: 0,
        isOpen: false,
        capacity: undefined,
        publicSlug: '',
        private: false,
      });
    }
    setModalStep('event-form');
  };

  const handleChooseFromTemplate = () => {
    setModalStep('select-template');
  };

  const handleSelectTemplate = (template: IEventTemplate) => {
    setSelectedTemplate(template);

    // Pre-fill form with template data but adjust dates to selected day
    if (selectedDate) {
      // Parse HH:MM time strings from template
      const [startHours, startMinutes] = template.startTime.split(':').map(Number);
      const [endHours, endMinutes] = template.endTime.split(':').map(Number);

      // Keep the time from template but use the selected date
      const newStart = new Date(selectedDate);
      newStart.setHours(startHours ?? 0, startMinutes ?? 0, 0, 0);

      const newEnd = new Date(selectedDate);
      newEnd.setHours(endHours ?? 0, endMinutes ?? 0, 0, 0);

      // If end time is before start time, it likely spans to next day
      if (newEnd <= newStart) {
        newEnd.setDate(newEnd.getDate() + 1);
      }

      setFormInitialData({
        name: template.name,
        description: template.description,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
        members: template.members || [],
        groups: template.groups || [],
        defaultAttendance: template.defaultAttendance ?? null,
        responsible: template.responsible || '',
        price: template.price || 0,
        isOpen: (template as any).isOpen || false,
        capacity: (template as any).capacity || undefined,
        publicSlug: '', // Generate new slug for each event from template
        private: false, // New events from templates default to non-private
      });
    }

    setModalStep('event-form');
  };

  // Consulted by Modal before Escape/backdrop/x dismisses the event form.
  const guardModalClose = (): boolean | void => {
    const onForm = modalStep === 'event-form' || modalStep === 'event-edit';
    if (!onForm || !formDirty) return;
    setDiscardConfirmOpen(true);
    return false;
  };

  const handleCloseModal = () => {
    setModalStep('closed');
    setFormDirty(false);
    setDiscardConfirmOpen(false);
    setSelectedDate(null);
    setSelectedDayEvents([]);
    setSelectedTemplate(null);
    setSelectedEvent(null);
    setFormInitialData(undefined);
    setError(null);
    setActiveTab('form');
  };

  const handleUpdateEvent = async (data: IEventFormData) => {
    if (!selectedEvent || !canEdit) return;

    setFormSubmitting(true);
    setError(null);

    try {
      await api.events.update(selectedEvent._id, {
        ...data,
        responsible: data.responsible || null,
      });

      // Refresh events and close modal
      await fetchEvents();
      handleCloseModal();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleDeleteEvent = () => {
    if (!selectedEvent || !canDelete) return;
    setDeleteConfirmOpen(true);
  };

  const handleDeleteEventConfirm = async () => {
    if (!selectedEvent || !canDelete) return;
    setDeleteLoading(true);
    try {
      await api.events.delete(selectedEvent._id);
      setDeleteConfirmOpen(false);
      await fetchEvents();
      handleCloseModal();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBackToDayEvents = () => {
    setModalStep('day-events');
    setFormDirty(false);
    setSelectedTemplate(null);
  };

  const handleBackToChoose = () => {
    setModalStep('choose-source');
    setFormDirty(false);
    setSelectedTemplate(null);
  };

  const handleBackToTemplates = () => {
    setModalStep('select-template');
    setFormDirty(false);
  };

  const handleCreateEvent = async (data: IEventFormData) => {
    if (!canCreate) return;

    setFormSubmitting(true);
    setError(null);

    try {
      await api.events.create({
        ...data,
        responsible: data.responsible || null,
      });

      // Refresh events and close modal
      await fetchEvents();
      handleCloseModal();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setFormSubmitting(false);
    }
  };

  const formatDisplayDate = (date: Date): string => {
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    const formatted = date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    // Capitalize first letter (Czech returns lowercase)
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const formatEventTime = (startTime: string, endTime: string): string => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';
    const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
    return `${start.toLocaleTimeString(locale, timeOptions)} - ${end.toLocaleTimeString(locale, timeOptions)}`;
  };

  const handleExportClick = async () => {
    if (!selectedEvent) return;

    setExportLoading(true);
    try {
      // Fetch attendance data
      const { data: attendanceData } = await apiClient.get<AttendanceData>(
        `/api/events/${selectedEvent._id}/attendance`
      );

      // Fetch full member data for export
      const { data: fullMembersData } = await api.members.list();

      // Create a map of member ID to full member data
      const memberDataMap = new Map((fullMembersData as any[]).map(m => [m._id, m]));

      // Collect all members from groups and direct members
      const allEventMembers: ExportableData[] = [];
      const attendanceStatusMap = new Map<string, boolean | null>();

      // Add members from groups
      for (const group of attendanceData.groups) {
        for (const member of group.members) {
          const fullMember = memberDataMap.get(member._id);
          if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
            allEventMembers.push(fullMember);
            const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
            attendanceStatusMap.set(member._id, status);
          }
        }
      }

      // Add direct members
      for (const member of attendanceData.directMembers) {
        const fullMember = memberDataMap.get(member._id);
        if (fullMember && !allEventMembers.find(m => m._id === member._id)) {
          allEventMembers.push(fullMember);
          const status = member.status === 'attending' ? true : member.status === 'not-attending' ? false : null;
          attendanceStatusMap.set(member._id, status);
        }
      }

      setExportData(allEventMembers);
      setAttendanceMap(attendanceStatusMap);
      setShowExportModal(true);
    } catch (err: any) {
      console.error('Failed to fetch export data:', err);
    } finally {
      setExportLoading(false);
    }
  };

  // Generate export fields from entity definition (includes attendance)
  const exportFields = useMemo(() =>
    getEventExportFields(memberFields, ['name', 'user.name', 'groups', 'attendance']),
  []);

  const getModalTitle = (): string => {
    if (modalStep === 'event-edit' && selectedEvent) {
      return `${t('common.edit')}: ${selectedEvent.name}`;
    }
    if (!selectedDate) return '';
    const dateStr = formatDisplayDate(selectedDate);

    switch (modalStep) {
      case 'day-events':
        return dateStr;
      case 'choose-source':
        return t('calendar.createEvent');
      case 'select-template':
        return t('calendar.selectTemplate');
      case 'event-form':
        return selectedTemplate ? t('calendar.newEventFrom', { templateName: selectedTemplate.name }) : t('calendar.newEvent');
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="calendar-page-loading">
        <LoadingSpinner size="lg" text={t('calendar.loading')} />
      </div>
    );
  }

  return (
    <div className="event-calendar-page">
      <PageHeader
        title={t('calendar.title')}
        helpKey="help.admin.calendar"
      />
      <Calendar
        year={currentYear}
        month={currentMonth}
        events={filteredEvents}
        onDayClick={handleDayClick}
        onEventClick={handleEventClick}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
        onToday={handleToday}
        filterContent={filterContent}
      />

      <Modal
        isOpen={modalStep !== 'closed'}
        onClose={handleCloseModal}
        onRequestClose={guardModalClose}
        title={getModalTitle()}
        size={modalStep === 'event-edit' || modalStep === 'event-form' ? 'xlarge' : 'small'}
      >
        {/* Day Events View */}
        {modalStep === 'day-events' && (
          <div className="modal-day-events">
            {selectedDayEvents.length === 0 ? (
              <EmptyState
                title={t('calendar.emptyTitle')}
                description={t('calendar.emptyDescription')}
                size="sm"
              />
            ) : (
              <div className="day-events-list">
                {selectedDayEvents.map((event) => {
                  // Attendance pill (reuses the same source as the month chip)
                  const totalMembers = event.totalMembers ?? 0;
                  const attendingCount = event.attendingCount ?? 0;
                  const hasAttendance = totalMembers > 0;
                  const attendanceRatio = hasAttendance ? attendingCount / totalMembers : 0;
                  const attendanceState =
                    attendingCount === 0 ? 'state-none'
                    : attendanceRatio >= 1 ? 'state-full'
                    : 'state-partial';

                  // Payment pill (admin events list exposes paid/unpaid counts)
                  const eventPrice = event.price ?? 0;
                  const paidCount = event.paidCount ?? 0;
                  const hasPayment = eventPrice > 0 && totalMembers > 0;
                  const paymentState =
                    paidCount === 0 ? 'state-none'
                    : paidCount >= totalMembers ? 'state-full'
                    : 'state-partial';

                  return (
                    <div
                      key={event._id}
                      className="day-event-item"
                      style={{ borderLeftColor: event.color || undefined, '--evc': event.color || 'transparent' } as React.CSSProperties}
                      onClick={() => handleEventClick(event)}
                    >
                      <div className="day-event-info">
                        <div className="day-event-name">{event.name}</div>
                        <div className="day-event-time">
                          {formatEventTime(event.startTime, event.endTime)}
                        </div>
                      </div>
                      <div className="day-event-pills">
                        {event.status && event.status !== 'scheduled' && (
                          <EventStatusBadge status={event.status} compact />
                        )}
                        {hasAttendance && (
                          <span className={`day-event-pill attendance-pill ${attendanceState}`}>
                            <i className="fas fa-user" aria-hidden="true"></i>
                            {attendingCount}/{totalMembers}
                          </span>
                        )}
                        {hasPayment && (
                          <span className={`day-event-pill payment-pill ${paymentState}`}>
                            <i className="fas fa-sack-dollar" aria-hidden="true"></i>
                            {paidCount}/{totalMembers}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {canCreate && (
              <button className="create-event-btn" onClick={handleCreateNewEvent}>
                + {t('events.createNew')}
              </button>
            )}
          </div>
        )}

        {/* Unified Event Form - handles both create and edit */}
        {(modalStep === 'event-edit' || modalStep === 'event-form') && (
          <div className="modal-event-edit-container">
            {/* Back button - only for create mode */}
            {modalStep === 'event-form' && (
              <button
                className="modal-back-btn"
                onClick={selectedTemplate ? handleBackToTemplates : handleBackToChoose}
              >
                {t('calendar.backButton')}
              </button>
            )}

            {/* Action Buttons - only for edit mode */}
            {modalStep === 'event-edit' && selectedEvent && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <EventStatusBadge status={selectedEvent.status} />
                <EventStatusActions
                  eventId={selectedEvent._id}
                  eventName={selectedEvent.name}
                  status={selectedEvent.status}
                  onChanged={fetchEvents}
                  appearance="labelled"
                />
                <Button variant="secondary" size="sm" onClick={() => setBroadcastOpen(true)}>
                  {t('announcements.sendToEvent')}
                </Button>
                <Button variant="secondary" size="sm" onClick={handleExportClick} disabled={exportLoading}>
                  {exportLoading ? t('common.loading') : t('export.exportMembers')}
                </Button>
              </div>
            )}

            {/* Mobile Tab Switcher - only for edit mode */}
            {modalStep === 'event-edit' && selectedEvent && (
              <div className="tab-switcher mobile-only">
                <button
                  className={activeTab === 'form' ? 'active' : ''}
                  onClick={() => setActiveTab('form')}
                >
                  {t('eventDetails.tabEdit')}
                </button>
                <button
                  className={activeTab === 'attendance' ? 'active' : ''}
                  onClick={() => setActiveTab('attendance')}
                >
                  {t('eventDetails.tabAttendance')}
                </button>
                <button
                  className={activeTab === 'comments' ? 'active' : ''}
                  onClick={() => setActiveTab('comments')}
                >
                  {t('eventDetails.tabComments')}
                </button>
              </div>
            )}

            <div className={`modal-event-edit-layout ${modalStep === 'event-form' ? 'modal-event-edit-layout--single-column' : ''}`}>
              <div className={`modal-edit-form ${activeTab === 'form' ? 'active' : ''}`}>
                {error && <p className="error-message">{t('common.error')}: {error}</p>}

                <EventForm
                  initialData={formInitialData}
                  onDirtyChange={setFormDirty}
                  onSubmit={modalStep === 'event-edit' ? handleUpdateEvent : handleCreateEvent}
                  loading={formSubmitting}
                  submitButtonText={modalStep === 'event-edit' ? t('common.save') : t('events.createButton')}
                  idPrefix={modalStep === 'event-edit' ? 'edit-event' : 'create-event'}
                  onDelete={modalStep === 'event-edit' && canDelete ? handleDeleteEvent : undefined}
                  deleteButtonText={t('common.delete')}
                  prefetchedMembers={allMembers}
                  prefetchedGroups={allGroups}
                  prefetchedAdmins={admins}
                  eventId={modalStep === 'event-edit' ? selectedEvent?._id : undefined}
                />
              </div>

              {/* Widgets column - only for edit mode */}
              {modalStep === 'event-edit' && selectedEvent && (
                <div className={`modal-edit-widgets ${activeTab === 'attendance' || activeTab === 'comments' ? 'active' : ''}`}>
                  <EventAnnouncements eventId={selectedEvent._id} />
                  <CollapsibleWidget
                    title={t('eventDetails.tabComments')}
                    defaultExpanded={true}
                    storageKey={`modal-event-${selectedEvent._id}-comments`}
                  >
                    <EventComments eventId={selectedEvent._id} />
                  </CollapsibleWidget>
                  <CollapsibleWidget
                    title={t('eventDetails.tabAttendance')}
                    defaultExpanded={true}
                    storageKey={`modal-event-${selectedEvent._id}-attendance`}
                  >
                    <AttendanceOverview
                      eventId={selectedEvent._id}
                      authToken={getUserInfo()?.token || ''}
                      eventStatus={selectedEvent.status}
                    />
                  </CollapsibleWidget>
                  <RequirementsWidget eventId={selectedEvent._id} />
                  <CollapsibleWidget
                    title={t('requirements.configTitle')}
                    defaultExpanded={false}
                    storageKey={`modal-event-${selectedEvent._id}-requirements-config`}
                  >
                    <EventRequirementsConfig eventId={selectedEvent._id} />
                  </CollapsibleWidget>
                  <CollapsibleWidget
                    title={t('roles.entityAccess')}
                    defaultExpanded={false}
                    storageKey={`modal-event-${selectedEvent._id}-role-access`}
                  >
                    <RoleAccessWidget entityType="events" entityId={selectedEvent._id} />
                  </CollapsibleWidget>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Choose source step */}
        {modalStep === 'choose-source' && (
          <div className="modal-choose-source">
            <button className="modal-back-btn" onClick={handleBackToDayEvents}>
              {t('calendar.backButton')}
            </button>
            <p className="modal-choose-prompt">{t('calendar.howToCreate')}</p>
            <div className="modal-choose-buttons">
              <button
                className="modal-choose-btn scratch"
                onClick={handleChooseFromScratch}
              >
                <span className="modal-choose-btn-icon">+</span>
                <span className="modal-choose-btn-text">{t('calendar.startFromScratch')}</span>
                <span className="modal-choose-btn-desc">{t('calendar.createBlankEvent')}</span>
              </button>
              <button
                className="modal-choose-btn template"
                onClick={handleChooseFromTemplate}
                disabled={templates.filter(tmpl => tmpl.isActive).length === 0}
              >
                <span className="modal-choose-btn-icon">+</span>
                <span className="modal-choose-btn-text">{t('calendar.useTemplate')}</span>
                <span className="modal-choose-btn-desc">
                  {templates.filter(tmpl => tmpl.isActive).length === 0
                    ? t('calendar.noTemplatesAvailable')
                    : t('calendar.templatesAvailable', { count: templates.filter(tmpl => tmpl.isActive).length })}
                </span>
              </button>
            </div>
          </div>
        )}

        {/* Select template step */}
        {modalStep === 'select-template' && (
          <div className="modal-select-template">
            <button className="modal-back-btn" onClick={handleBackToChoose}>
              {t('calendar.backButton')}
            </button>
            <div className="template-list">
              {templates.filter(tmpl => tmpl.isActive).map((template) => (
                <div
                  key={template._id}
                  className="template-item"
                  onClick={() => handleSelectTemplate(template)}
                >
                  <div className="template-item-name">{template.name}</div>
                  <div className="template-item-desc">{template.description}</div>
                  <div className="template-item-time">
                    {template.startTime} - {template.endTime} ({template.recurringPattern})
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </Modal>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename={`event-${selectedEvent?.name.replace(/\s+/g, '-').toLowerCase() || 'members'}-${new Date().toISOString().split('T')[0]}`}
        availableFields={exportFields}
        title={t('export.eventExportTitle')}
        attendanceMap={attendanceMap}
      />

      {selectedEvent && (
        <BroadcastModal
          isOpen={broadcastOpen}
          onClose={() => setBroadcastOpen(false)}
          preselectedEventId={selectedEvent._id}
          preselectedEventName={selectedEvent.name}
        />
      )}

      <ConfirmModal
        isOpen={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        onConfirm={handleCloseModal}
        title={t('unsavedChanges.title')}
        message={t('unsavedChanges.message')}
        confirmText={t('unsavedChanges.discard')}
        cancelText={t('unsavedChanges.keepEditing')}
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteEventConfirm}
        title={t('events.deleteTitle')}
        message={t('events.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default EventCalendarPage;
